import type { Doc } from "building-types/doc";
import type { BuildingGenerationError } from "building-types/errors/buildingGenerationError";
import {
  BuildingGenerationErrorBase,
  BuildingGenerationErrorType,
} from "building-types/errors/buildingGenerationError";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { RoomOutlineProblem } from "building-types/rooms/checkRoomOutline";
import type { BuildingGenerationIssue, ParamDetails } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import { MIN_VALID_SHUTTER_HEIGHT, MIN_VALID_SHUTTER_WIDTH } from "common/constants/buildingObjects";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { docGetEntityBreadcrumbString } from "lib/studio/entityName";

function formatIssueParam(param: ParamDetails) {
  switch (param.type) {
    case "RANGE": {
      switch (param.issueType) {
        case "MAX_EXCEEDED": {
          return `${param.description} ${param.value} is greater than max value ${param.max}`;
        }
        case "MIN_EXCEEDED": {
          return `${param.description} ${param.value} is less than min value ${param.min}`;
        }
        default: {
          return `${param.description}: ${param.min} <= ${param.value} <= ${param.max}`;
        }
      }
    }
    case "SET": {
      return `${param.value} not valid in set ${param.allowedSet.join(", ")}`;
    }
    default: {
      return JSON.stringify(param);
    }
  }
}

export function formatIssueParams(issue: BuildingGenerationIssue) {
  switch (issue.type) {
    case BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY:
    case BuildingGenerationIssueType.DOOR_OPENING_EXCEEDS_BOUNDARY:
    case BuildingGenerationIssueType.SKYLIGHT_OPENING_EXCEEDS_BOUNDARY: {
      const params = issue.params ?? [];
      const result: string[] = [];
      for (const param of params) {
        result.push(formatIssueParam(param));
      }
      return result.join("; ");
    }
    default:
      return "";
  }
}

export function formatBuildingErrorMessage(doc: Doc, err: Error | undefined): string {
  if (!err) {
    return "No error";
  }

  if (err instanceof BuildingGenerationErrorBase) {
    const e = err as BuildingGenerationError;

    switch (e.type) {
      case BuildingGenerationErrorType.INVALID_PLANAR_GRAPH_LOOPS: {
        const level = doc.levels[e.levelHandle.id];
        if (!level) {
          return "Detected an invalid room outline condition on an unknown level. There may be snapping issues.";
        }
        return `Detected an invalid room outline condition on Level "${level.name}". There may be snapping issues.`;
      }
      case BuildingGenerationErrorType.INVALID_ROOM_OUTLINE: {
        switch (e.problem) {
          case RoomOutlineProblem.AREA_TOO_SMALL: {
            return `Room "${docGetEntityBreadcrumbString(doc, e.roomHandle)}" has an area that is too small.`;
          }
          case RoomOutlineProblem.CW_WINDING: {
            return `Room "${docGetEntityBreadcrumbString(
              doc,
              e.roomHandle,
            )}" has a clockwise winding, but it should be counterclockwise. `;
          }
          case RoomOutlineProblem.SELF_INTERSECTS: {
            return `Room "${docGetEntityBreadcrumbString(doc, e.roomHandle)}" has a self-intersecting outline.`;
          }
          case RoomOutlineProblem.LIKELY_SELF_INTERSECTS: {
            return `Room "${docGetEntityBreadcrumbString(
              doc,
              e.roomHandle,
            )}" likely has a self-intersecting outline and has caused an error.`;
          }
          case RoomOutlineProblem.DEGENERATED: {
            return `Room "${docGetEntityBreadcrumbString(doc, e.roomHandle)}" has a degenerate outline.`;
          }
          default: {
            assertUnreachable(e.problem);
          }
        }
      }
      case BuildingGenerationErrorType.OVERLAPPING_ROOMS_ERROR: {
        const level = doc.levels[e.levelHandle.id];

        // proactive check due to an error where the level was null.
        if (!level) {
          return `Overlapping rooms encountered`;
        }
        return `Overlapping rooms encountered on level "${level.name}"`;
      }
      case BuildingGenerationErrorType.HOLE_ON_LEVEL: {
        const level = doc.levels[e.levelHandle.id];

        // proactive check due to an error where the level was null.
        if (!level) {
          return `A room hole was detected on level`;
        }
        return `A room hole was detected on level "${level.name}".`;
      }
      case BuildingGenerationErrorType.MISSING_EXPRESSION_OPTION_SET_ERROR: {
        return `Unrecognized parameter "${e.variableName}" in formula "${e.formula}"`;
      }
      case BuildingGenerationErrorType.EXPRESSION_SYNTAX_ERROR: {
        return `Bad formula syntax: ${e.formula} | ${e.message} | context: ${e.contextString}`;
      }
      default:
        if (!("error type does not have message defined")) {
          console.assert(false);
        }
    }
  }

  return err.message;
}

export function formatBuildingGenerationIssueMessage(
  issue: BuildingGenerationIssue,
  docGetEntityName: (handle: DocEntityHandle | undefined) => string,
): string {
  switch (issue?.type) {
    case BuildingGenerationIssueType.ROOM_OVERLAP: {
      return "Overlapping rooms are not allowed and will be ignored.";
    }
    case BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY: {
      return "Window opening exceeds its wall boundary and will be omitted";
    }
    case BuildingGenerationIssueType.DOOR_OPENING_EXCEEDS_BOUNDARY: {
      return "Door opening exceeds its wall boundary and will be omitted";
    }
    case BuildingGenerationIssueType.STAIRWAY_CONNECTION_ISSUE: {
      return `There is no matching top room for stairway on level ${issue.missingRoomOnLevel} for room ${issue.missingRoomOnLevel}. The stairway will be omitted.`;
    }
    case BuildingGenerationIssueType.STAIRWAY_ENTRANCE_WALL_MISSING: {
      return "Stairway entrance wall is missing";
    }
    case BuildingGenerationIssueType.STAIR_RUN_TYPE_INCOMPATIBLE: {
      return "Stair run with non-rectangular outline cannot be Assembled or Monolithic";
    }
    case BuildingGenerationIssueType.STAIR_PLACEMENT_ROOM_MISSING: {
      return "Stair placement references a missing room";
    }
    case BuildingGenerationIssueType.STAIR_GENERATION_ISSUE: {
      const stairBlockName = docGetEntityName(issue.handle);
      return `Stair generation issue -- ${issue.message}: ${stairBlockName}`;
    }
    case BuildingGenerationIssueType.STAIRWAY_MISSING_LEVEL: {
      const levelName = docGetEntityName(issue.handle);
      return `Stairway level is missing: ${levelName}`;
    }
    case BuildingGenerationIssueType.BLOCK_MISSING_LEVEL: {
      const blockName = docGetEntityName(issue.handle);
      if (issue.docRefName !== undefined && issue.docRefName !== null) {
        return `Block level is missing: ${blockName} (imported from doc ref "${issue.docRefName}")`;
      }

      return `Block level is missing: ${blockName}`;
    }
    case BuildingGenerationIssueType.RAILING_LEVEL_UNDEFINED: {
      return `Railing level is undefined`;
    }
    case BuildingGenerationIssueType.UNKNOWN_WINDOW_TYPE: {
      return `Unknown window type: ${issue.windowType}`;
    }
    case BuildingGenerationIssueType.MISSING_PROFILE_REFERENCE: {
      return `Unknown profile: ${issue.profileId}`;
    }
    case BuildingGenerationIssueType.UNKNOWN_DOOR_TYPE: {
      return `Unknown door type: ${issue.doorType}`;
    }
    case BuildingGenerationIssueType.INVALID_DOOR_HEIGHT: {
      return `Door type ${issue.doorType} is invalid: ${issue.message}`;
    }
    case BuildingGenerationIssueType.UNKNOWN_CASEWORK_TYPE: {
      return `Unknown casework type: ${issue.caseworkType}`;
    }
    case BuildingGenerationIssueType.INVALID_WINDOW_SASH_GEOMETRY: {
      return `Window sash is not wide enough to be created. Decrease vertical division spacing or increase window width.`;
    }
    case BuildingGenerationIssueType.INVALID_WINDOW_SHUTTER_SIZE: {
      return `Window shutter width must be at least ${MIN_VALID_SHUTTER_WIDTH} inches`;
    }
    case BuildingGenerationIssueType.INVALID_WINDOW_SHUTTER_HEIGHT: {
      return `Window shutter height must be at least ${MIN_VALID_SHUTTER_HEIGHT} inches`;
    }
    case BuildingGenerationIssueType.HOLE_ON_LEVEL: {
      return `${issue.holes.length} room hole(s) were found on level.`;
    }
    case BuildingGenerationIssueType.INVALID_WINDOW_SHUTTER_GEOMETRY: {
      return `Window shutter geometry is invalid`;
    }
    case BuildingGenerationIssueType.SKYLIGHT_OPENING_EXCEEDS_BOUNDARY: {
      return "Skylight opening exceeds its roof boundary and will be omitted";
    }
    case BuildingGenerationIssueType.INVALID_FOUNDATION_CONDITION: {
      return `Foundation system contains invalid condition: ${issue.invalidCondition}. Falling back to ${issue.fallbackCondition}`;
    }
    case BuildingGenerationIssueType.INVALID_ANNOTATION_EDGE_ID: {
      return `Annotation Edge Id "${issue.edgeId}" is invalid`;
    }
    case BuildingGenerationIssueType.INVALID_FREE_FORM_ROOF_PERIMETER: {
      return `Free form roof perimeter is invalid`;
    }
    case BuildingGenerationIssueType.COLUMN_GENERATION_ISSUE: {
      return `Column generation issue: ${issue.message}`;
    }
    case BuildingGenerationIssueType.MEASUREMENT_GENERATION_ISSUE: {
      return `Measurement generation issue: ${issue.message}`;
    }
    case BuildingGenerationIssueType.ROOF_EDGE_SLOPE_ISSUE: {
      return "Roof edge slope is too small and will be omitted";
    }
    case BuildingGenerationIssueType.INVALID_DATUM: {
      return `Invalid datum : ${issue.message}`;
    }
    case BuildingGenerationIssueType.LEVEL_CIRCULAR_DEPENDENCY: {
      return `Circular dependency detected in level stacking: ${issue.message}`;
    }
    case BuildingGenerationIssueType.ELECTRICAL_RULE_DUPLICATE_ID: {
      const settingsName = docGetEntityName(issue.handle);
      return `Electrical rules in ${settingsName} have the same id. Only the last will be used if they are all enabled. Assigning unique ids is recommended.`;
    }
    case BuildingGenerationIssueType.FETCH_ISSUE: {
      const { message, handle } = issue;
      return `Fetch timed out: ${message} --- Handle: ${JSON.stringify(handle)}`;
    }
    case BuildingGenerationIssueType.ANNOTATION_MISSING_PARENT: {
      const annotationName = docGetEntityName(issue.handle);
      return `Annotation ${annotationName} is missing a parent`;
    }
    case BuildingGenerationIssueType.CEILING_GENERATION_ISSUE: {
      return `Ceiling generation issue: ${issue.message}`;
    }
    case BuildingGenerationIssueType.FLOOR_GENERATION_ERROR: {
      return `Floor Horizontal Assembly generation error: ${issue.message}`;
    }
    case BuildingGenerationIssueType.WALL_FRAMING_GENERATION_ERROR: {
      return `Wall framing generation error: ${issue.message}`;
    }
    case BuildingGenerationIssueType.ROOF_SYSTEM_GENERATION_ERROR: {
      return `Roof system generation error: ${issue.message}`;
    }
    default:
      console.assert(false, "issue type does not have message defined");
  }
  return "";
}
